import { useState } from "react";
import { money, dateWithAgo, dateShort } from "@/lib/format";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Navigation, MapPinCheck, MessageSquare, ShoppingBag, MoreVertical, ThumbsDown, Info, Store, Radar,
  AlertTriangle, TrendingDown, Sparkles, CheckCircle2,
} from "lucide-react";

const TIER_STYLE = {
  stopped: { cls: "bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]", icon: AlertTriangle },
  slowed: { cls: "bg-[#FEF3C7] text-[#B45309] border-[#FCD34D]", icon: TrendingDown },
  prospect: { cls: "bg-[#DBEAFE] text-[#1E40AF] border-[#93C5FD]", icon: Sparkles },
  healthy: { cls: "bg-[#D1FAE5] text-[#047857] border-[#A7F3D0]", icon: CheckCircle2 },
};

// One garage card, two variants. The only thing that changes is the third button.
export default function GarageCard({ g, showBadge = false, onOpen, onCheckin, onConvert, onOrder, onNotInterested }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const isCustomer = g.status === "group_created" || g.kind === "customer" || g.display_status === "group_created";
  const t = TIER_STYLE[g.tier] || null;
  const stop = (fn) => (e) => { e.stopPropagation(); fn && fn(g); };
  // 2.4 — Navigate opens the Google Business listing (photos/hours/reviews), not driving directions
  const profileUrl = g.google_maps_uri
    || (g.google_place_id
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(g.name || "")}&query_place_id=${g.google_place_id}`
      : (g.latitude != null ? `https://www.google.com/maps/search/?api=1&query=${g.latitude},${g.longitude}` : null));

  const dates = isCustomer
    ? `Last visit ${dateWithAgo(g.last_visit_date, "never")}`
    : ((g.visit_count || 0) > 0
        ? `Last visit ${dateWithAgo(g.last_visit_date, "never")}`
        : `Added ${dateShort(g.created_at, "—")} · never visited`);

  const btn = "flex-1 min-h-[44px] rounded-lg border-2 font-bold text-xs flex flex-col items-center justify-center gap-0.5 active:scale-[0.98] transition-transform";

  return (
    <div onClick={() => onOpen && onOpen(g.id)} data-testid={`garage-card-${g.id}`}
      className="bg-white border border-[#E5E7EB] rounded-xl p-3 cursor-pointer">
      {showBadge && (
        <div className="flex items-center justify-between mb-1.5">
          {t ? (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase border ${t.cls}`}>
              <t.icon className="w-3 h-3" /> {g.tier_label}
            </span>
          ) : isCustomer ? (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black uppercase bg-[#D1FAE5] text-[#047857] border border-[#6EE7B7]">
              <Store className="w-3.5 h-3.5" /> Customer
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black uppercase bg-[#DBEAFE] text-[#1E40AF] border border-[#93C5FD]">
              <Radar className="w-3.5 h-3.5" /> Prospect
            </span>
          )}
          {!isCustomer && g.opportunity_score != null && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#111827] text-white text-[11px] font-black" data-testid={`card-score-${g.id}`}>
              Score {g.opportunity_score}
            </span>
          )}
        </div>
      )}

      <div className="font-bold text-[15px] leading-[1.3] text-[#111827] line-clamp-2">{g.name}</div>
      <div className="text-[13px] text-[#6B7280]">{g.area}{g.distance_km != null ? ` · ${g.distance_km} km` : ""}</div>

      {isCustomer && (
        <div className="text-[13px] text-[#374151] mt-1">
          {g.visit_count || 0} visits · {g.purchase_count || 0} orders · <b className="text-[#047857]">{money(g.total_purchase_value)}</b>
        </div>
      )}
      <div className="text-[12px] text-[#9CA3AF] mt-0.5" data-testid={`card-dates-${g.id}`}>{dates}</div>

      <div className="flex gap-2 mt-3">
        {profileUrl && (
          <a href={profileUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
            data-testid={`card-navigate-${g.id}`} className={`${btn} border-[#1E3A8A] text-[#1E3A8A] bg-white`}>
            <Navigation className="w-4 h-4" /> Navigate
          </a>
        )}
        <button onClick={stop(onCheckin)} data-testid={`card-checkin-${g.id}`}
          className={`${btn} border-[#1E3A8A] bg-[#1E3A8A] text-white`}>
          <MapPinCheck className="w-4 h-4" /> Check in
        </button>
        {isCustomer ? (
          <button onClick={stop(onOrder)} data-testid={`card-order-${g.id}`}
            className={`${btn} border-[#1E3A8A] text-[#1E3A8A] bg-white`}>
            <ShoppingBag className="w-4 h-4" /> Order
          </button>
        ) : (
          <button onClick={stop(onConvert)} data-testid={`card-convert-${g.id}`}
            className={`${btn} border-[#047857] bg-[#047857] text-white`}>
            <MessageSquare className="w-4 h-4" /> Convert
          </button>
        )}
        <Popover open={menuOpen} onOpenChange={setMenuOpen}>
          <PopoverTrigger asChild>
            <button onClick={(e) => e.stopPropagation()} data-testid={`card-more-${g.id}`}
              className="w-11 min-h-[44px] rounded-lg border-2 border-[#E5E7EB] text-[#6B7280] flex items-center justify-center">
              <MoreVertical className="w-4 h-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-52 p-1" onClick={(e) => e.stopPropagation()}>
            <button onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onOpen && onOpen(g.id); }} data-testid={`card-details-${g.id}`}
              className="w-full flex items-center gap-2 px-3 h-11 rounded-md text-sm font-medium text-[#374151] hover:bg-[#F9FAFB]">
              <Info className="w-4 h-4" /> Open details
            </button>
            {!isCustomer && (
              <button onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onNotInterested && onNotInterested(g); }} data-testid={`card-not-interested-${g.id}`}
                className="w-full flex items-center gap-2 px-3 h-11 rounded-md text-sm font-medium text-[#B91C1C] hover:bg-[#FEE2E2]">
                <ThumbsDown className="w-4 h-4" /> Mark not interested
              </button>
            )}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
